import { apiClient } from './authService';

const sessionService = {
  getAll: async () => {
    const response = await apiClient.get('/sessions/');
    return response.data;
  },

  getById: async (id) => {
    const response = await apiClient.get(`/sessions/${id}/`);
    return response.data;
  },

  create: async (data) => {
    const response = await apiClient.post('/sessions/', data);
    return response.data;
  },

  listInstructors: async (params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    const url = queryParams ? `/instructors/?${queryParams}` : '/instructors/';
    const response = await apiClient.get(url);
    return response.data;
  },

  getBudget: async (id) => {
    const response = await apiClient.get(`/sessions/${id}/budget/`);
    return response.data;
  },

  validate: async (id) => {
    const response = await apiClient.post(`/sessions/${id}/validate/`);
    return response.data;
  },

  reject: async (id, reason) => {
    const response = await apiClient.post(`/sessions/${id}/reject/`, { reason });
    return response.data;
  },

  reset: async (id) => {
    const response = await apiClient.post(`/sessions/${id}/reset/`);
    return response.data;
  },

  cancel: async (id) => {
    const response = await apiClient.post(`/sessions/${id}/cancel/`);
    return response.data;
  },

  reopenToVerification: async (id) => {
    const response = await apiClient.post(`/sessions/${id}/reopen_to_verification/`);
    return response.data;
  },

  resubmitForVerification: async (id) => {
    const response = await apiClient.post(`/sessions/${id}/resubmit_for_verification/`);
    return response.data;
  },

  markVerified: async (id) => {
    const response = await apiClient.post(`/sessions/${id}/mark_verified/`);
    return response.data;
  },

  requestModification: async (id, note) => {
    const response = await apiClient.post(`/sessions/${id}/request_modification/`, { note });
    return response.data;
  },

  requestModificationManager: async (id, note) => {
    const response = await apiClient.post(`/sessions/${id}/request_modification_manager/`, { note });
    return response.data;
  },

  rejectVerification: async (id, reason = '') => {
    const response = await apiClient.post(`/sessions/${id}/reject_verification/`, { reason });
    return response.data;
  },

  update: async (id, data) => {
    const response = await apiClient.patch(`/sessions/${id}/`, data);
    return response.data;
  },

  delete: async (id) => {
    const response = await apiClient.delete(`/sessions/${id}/`);
    return response.data;
  },

  setSupplierDeliveryDates: async (sessionId, deliveryDates = []) => {
    const response = await apiClient.post(`/sessions/${sessionId}/set_supplier_delivery_dates/`, {
      delivery_dates: deliveryDates,
    });
    return response.data;
  },

  adjustStockConsumption: async (sessionId, payload = {}) => {
    const response = await apiClient.post(`/sessions/${sessionId}/stock_adjustment/`, payload);
    return response.data;
  },

  syncSupplierOrders: async (sessionId) => {
    const response = await apiClient.post(`/sessions/${sessionId}/sync_supplier_orders/`);
    return response.data;
  },

  downloadInvoice: async (sessionId, supplierId, invoiceParams = {}, orderId = null) => {
    const response = await apiClient.post(`/sessions/${sessionId}/generate_invoice/`, {
      supplier_id: supplierId,
      invoice_params: invoiceParams,
      ...(orderId ? { order_id: orderId } : {}),
    }, {
      responseType: 'blob'
    });
    return response;
  },

  downloadAllInvoices: async (sessionId, invoiceParams = {}) => {
    const response = await apiClient.post(`/sessions/${sessionId}/generate_all_invoices/`, {
      invoice_params: invoiceParams,
    }, {
      responseType: 'blob'
    });
    return response;
  },

  downloadConsolidatedInvoice: async (sessionId, lang) => {
    const response = await apiClient.post(
      `/sessions/${sessionId}/generate_consolidated_invoice/`,
      { lang },
      { responseType: 'blob' }
    );
    return response;
  },

  supplierOrders: async (params = {}) => {
    const qp = new URLSearchParams();
    Object.entries(params || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') qp.append(key, String(value));
    });
    const url = qp.toString() ? `/sessions/supplier_orders/?${qp.toString()}` : '/sessions/supplier_orders/';
    const response = await apiClient.get(url);
    return response.data;
  },

  accountingReport: async (params = {}) => {
    const qp = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => {
      if (v === undefined || v === null || v === '') return;
      if (Array.isArray(v)) {
        if (v.length === 0) return;
        qp.append(k, v.join(','));
        return;
      }
      qp.append(k, String(v));
    });
    const url = qp.toString() ? `/sessions/accounting_report/?${qp.toString()}` : '/sessions/accounting_report/';
    const response = await apiClient.get(url);
    return response.data;
  },

  supplierOrderDetail: async (orderId) => {
    const response = await apiClient.get(`/sessions/supplier_orders/${orderId}/`);
    return response.data;
  },

  accountingDashboard: async (params) => {
    const query = new URLSearchParams();

    if (params?.months) query.append('months', String(params.months));
    if (params?.date_from) query.append('date_from', params.date_from);
    if (params?.date_to) query.append('date_to', params.date_to);
    if (Array.isArray(params?.centre_ids)) {
      const ids = params.centre_ids.filter(Boolean).map(String);
      if (ids.length) query.append('centre_ids', ids.join(','));
    }

    const response = await apiClient.get(`/sessions/accounting_dashboard/?${query.toString()}`);
    return response.data;
  },

  supplierOrderInvoice: async (orderId) => {
    const response = await apiClient.get(`/sessions/supplier_orders/${orderId}/invoice/`, {
      responseType: 'blob'
    });
    return response;
  },

  updateSupplierOrderStatus: async (orderId, status) => {
    const response = await apiClient.post(`/sessions/supplier_orders/${orderId}/status/`, { status });
    return response.data;
  },

  listAdditionalFees: async (params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    const url = queryParams ? `/additional_fees/?${queryParams}` : '/additional_fees/';
    const response = await apiClient.get(url);
    return response.data;
  },

  createAdditionalFee: async (payload = {}) => {
    const form = new FormData();
    Object.entries(payload || {}).forEach(([k, v]) => {
      if (v === undefined || v === null || v === '') return;
      form.append(k, v);
    });
    const response = await apiClient.post('/additional_fees/', form);
    return response.data;
  },

  uploadSessionDocument: async (sessionId, file) => {
    const form = new FormData();
    form.append('session', sessionId);
    form.append('file', file);
    const response = await apiClient.post('/session_documents/', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  deleteSessionDocument: async (documentId) => {
    const response = await apiClient.delete(`/session_documents/${documentId}/`);
    return response.data;
  },

  downloadSessionDocument: async (documentId) => {
    const response = await apiClient.get(`/session_documents/${documentId}/download/`, {
      responseType: 'blob',
    });
    return response;
  },

  updateAdditionalFee: async (feeId, payload = {}) => {
    const form = new FormData();
    Object.entries(payload || {}).forEach(([k, v]) => {
      if (v === undefined || v === null) return;
      if (k === 'attachment' && v === '') return;
      form.append(k, v);
    });
    const response = await apiClient.patch(`/additional_fees/${feeId}/`, form);
    return response.data;
  },

  deleteAdditionalFee: async (feeId) => {
    const response = await apiClient.delete(`/additional_fees/${feeId}/`);
    return response.data;
  },

  verifyAdditionalFee: async (feeId, note = '') => {
    const response = await apiClient.post(`/additional_fees/${feeId}/verify/`, { note });
    return response.data;
  },

  validateAdditionalFee: async (feeId, note = '') => {
    const response = await apiClient.post(`/additional_fees/${feeId}/validate/`, { note });
    return response.data;
  },

  rejectAdditionalFee: async (feeId, note = '') => {
    const response = await apiClient.post(`/additional_fees/${feeId}/reject/`, { note });
    return response.data;
  },
};

export default sessionService;
